/**
 * Huawei disk barcode helpers for the editor UI.
 *
 * Mirrors server/src/patching/barcode-sn.ts (the canonical port of
 * NvmeReserveEditor/Model/BarcodeSn.cs). Fixed 21-char layout:
 *   ① snId(2)="21" ② material(8)=BOM ③ vendor(2) ④ dateCode(2: year+month)
 *   ⑤ serial(6) ⑥ rohs(1) ⑦ internalModel(variable, often space-separated)
 */

export const SN_ID_LEN = 2;
export const MATERIAL_LEN = 8;
export const VENDOR_LEN = 2;
export const DATE_LEN = 2;
export const SERIAL_LEN = 6;

// Year chars: '1'..'9' → 2001..2009, then letters skipping I, O, Z.
const YEAR_CHARS = '123456789ABCDEFGHJKLMNPQRSTUVWXY';
// Month chars: '1'..'9' → January..September, 'A'..'C' → October..December.
const MONTH_CHARS = '123456789ABC';
export const MIN_YEAR = 2001;
export const MAX_YEAR = MIN_YEAR + YEAR_CHARS.length - 1; // 2032

export interface Barcode {
  snId: string;
  material: string;
  vendor: string;
  dateCode: string;
  serial: string;
  rohs: string;
  internalModel: string;
  modelSpaced: boolean;
}

export function parseBarcode(sn: string): Barcode {
  let p = 0;
  const take = (len: number): string => {
    if (p >= sn.length) return '';
    const t = Math.min(len, sn.length - p);
    const r = sn.substring(p, p + t);
    p += t;
    return r;
  };
  const b: Barcode = {
    snId: take(SN_ID_LEN),
    material: take(MATERIAL_LEN),
    vendor: take(VENDOR_LEN),
    dateCode: take(DATE_LEN),
    serial: take(SERIAL_LEN),
    rohs: '',
    internalModel: '',
    modelSpaced: true,
  };
  if (p < sn.length) {
    b.rohs = sn.substring(p, p + 1);
    p++;
  }
  if (p < sn.length) {
    b.modelSpaced = sn[p] === ' ';
    if (b.modelSpaced) p++;
    b.internalModel = sn.substring(p);
  }
  return b;
}

export function buildBarcode(b: Barcode): string {
  let s = b.snId + b.material + b.vendor + b.dateCode + b.serial + b.rohs;
  if (b.internalModel) {
    if (b.modelSpaced) s += ' ';
    s += b.internalModel;
  }
  return s;
}

export function encodeYear(year: number): string | null {
  const idx = year - MIN_YEAR;
  return idx < 0 || idx >= YEAR_CHARS.length ? null : YEAR_CHARS[idx]!;
}

export function encodeMonth(month: number): string | null {
  const idx = month - 1;
  return idx < 0 || idx >= MONTH_CHARS.length ? null : MONTH_CHARS[idx]!;
}

export function decodeYear(c: string): number | null {
  const idx = YEAR_CHARS.indexOf(c.toUpperCase());
  return idx < 0 ? null : MIN_YEAR + idx;
}

export function decodeMonth(c: string): number | null {
  const idx = MONTH_CHARS.indexOf(c.toUpperCase());
  return idx < 0 ? null : idx + 1;
}

/** "2023-07-15", "2023/7", "2023.07" … → the two encoded dateCode chars. */
export function dateCodeFromManufactured(s: string): string | null {
  const m = /(\d{4})\s*[-/.]\s*(\d{1,2})/.exec(s);
  if (!m) return null;
  const y = encodeYear(Number(m[1]));
  const mo = encodeMonth(Number(m[2]));
  return y && mo ? y + mo : null;
}

/** Human-readable decode of the 2 dateCode chars, '' when not decodable. */
export function decodeDateCode(dateCode: string): string {
  if (dateCode.length < DATE_LEN) return '';
  const y = decodeYear(dateCode[0]!);
  const m = decodeMonth(dateCode[1]!);
  if (y === null || m === null) return '';
  return `${y}-${String(m).padStart(2, '0')}`;
}

/**
 * Validate a parsed barcode against the fixed Huawei layout. Returns a list of
 * human-readable warnings; empty means well-formed. Mirrors the canonical
 * server-side BarcodeSn.validate() so the editor refuses to ship a barcode the
 * server would later flag. The internal model / RoHS are free-form and not
 * checked here.
 */
export function validateBarcode(b: Barcode): string[] {
  const w: string[] = [];
  if (b.snId !== '21') w.push(`SN ID is "${b.snId}", expected "21"`);
  if (b.material.length !== MATERIAL_LEN)
    w.push(`BOM (material) is ${b.material.length} chars, expected ${MATERIAL_LEN}`);
  if (b.vendor.length !== VENDOR_LEN)
    w.push(`vendor code is ${b.vendor.length} chars, expected ${VENDOR_LEN}`);
  if (b.dateCode.length !== DATE_LEN) {
    w.push(`date code is ${b.dateCode.length} chars, expected ${DATE_LEN}`);
  } else {
    if (decodeYear(b.dateCode[0]!) === null)
      w.push(`invalid year char "${b.dateCode[0]}" in date code`);
    if (decodeMonth(b.dateCode[1]!) === null)
      w.push(`invalid month char "${b.dateCode[1]}" in date code`);
  }
  if (b.serial.length !== SERIAL_LEN)
    w.push(`serial is ${b.serial.length} chars, expected ${SERIAL_LEN}`);
  return w;
}

/** Validate a raw barcode string. Convenience wrapper over parse+validate. */
export function validateBarcodeString(sn: string): string[] {
  return validateBarcode(parseBarcode(sn));
}

/**
 * Increment the trailing digit run of a serial, preserving its width:
 * "000124" → "000125", "AB0009" → "AB0010". Returns null when the serial
 * has no digits to iterate or the run overflows its width.
 */
export function incrementSerial(serial: string, by: number): string | null {
  const m = /^(.*?)(\d+)$/.exec(serial);
  if (!m) return null;
  const next = String(Number(m[2]) + by).padStart(m[2]!.length, '0');
  if (next.length > m[2]!.length) return null;
  return m[1]! + next;
}
